import { ActionTree, CommitOptions, ActionContext } from "vuex";
import { State as RootState } from "@/store";
import { State as LocalState, Idea, Solution, Resolution } from "./state";
import { Challenge } from "../challenges/state";
import { Mutations } from "./mutations";
import { ActionTypes } from "./action-types";
import { MutationTypes } from "./mutation-types";
import router from "@/router";
import {
  getChallenge,
  updateChallenge,
  getAIChallengeTitle,
  addIdea,
  deleteIdea,
  updateIdea,
  addSolution,
  deleteSolution,
  updateSolution,
  setIdeaFavorite,
  resolveChallenge,
  deleteChallenge,
  generatePlan,
} from "@/services/challenges.service";
import { getEntitiesNlp } from "@/services/entities.service";
import { captureException } from "@/utils/errors";
import { truncate } from "@/utils/text";

export type UpdateableChallenge = {
  _id: string;
  quiet?: boolean;
  data: {
    title: string;
    subject: string;
    description: string;
    additional: string;
    types: string[];
    tags: string[];
    resolution: string;
    status: number;
  };
};

export type IdeaForm = {
  description: string;
  notes: string;
  origin: string;
  sparkText: string;
  stem: Record<string, string>;
};

export type UpdateableIdea = {
  _id: string;
  data: {
    description: string;
    notes: string;
  };
};

export type FavoriteIdea = {
  _id: string;
  data: {
    isFavorite: boolean;
  };
};

export type SolutionForm = {
  ideas: string[];
  description: string;
  sharedWith: string;
  successCriteria: string;
  targetDate: string;
};

export type UpdateableSolution = {
  _id: string;
  data: {
    ideas: string[];
    description: string;
    sharedWith: string;
    successCriteria: string;
    targetDate: string;
  };
};

export type ResolutionForm = {
  _id: string;
  resolution: Resolution;
};

export type GeneratePlan = {
  challenge: string;
  actor: string;
  ideas: string[];
  steps: number;
};

// Actions context
type AugmentedActionContext = {
  commit<K extends keyof Mutations>(
    key: K,
    payload?: Parameters<Mutations[K]>[1],
    options?: CommitOptions,
  ): ReturnType<Mutations[K]>;
} & Omit<ActionContext<LocalState, RootState>, "commit">;

// Actions contracts
export interface Actions {
  [ActionTypes.GET_CHALLENGE](
    { commit, dispatch }: AugmentedActionContext,
    payload: string,
  ): void;
  [ActionTypes.UPDATE_CHALLENGE](
    { commit, dispatch }: AugmentedActionContext,
    payload: UpdateableChallenge,
  ): void;
  [ActionTypes.GET_AI_CHALLENGE_TITLE]({
    commit,
    dispatch,
  }: AugmentedActionContext): void;
  [ActionTypes.ADD_IDEA](
    { commit, dispatch }: AugmentedActionContext,
    payload: IdeaForm,
  ): void;
  [ActionTypes.DELETE_IDEA](
    { commit, dispatch }: AugmentedActionContext,
    payload: Idea,
  ): void;
  [ActionTypes.UPDATE_IDEA](
    { commit, dispatch }: AugmentedActionContext,
    payload: UpdateableIdea,
  ): void;
  [ActionTypes.ADD_SOLUTION](
    { commit, dispatch }: AugmentedActionContext,
    payload: SolutionForm,
  ): Promise<string | void>;
  [ActionTypes.DELETE_SOLUTION](
    { commit, dispatch }: AugmentedActionContext,
    payload: Solution,
  ): void;
  [ActionTypes.UPDATE_SOLUTION](
    { commit, dispatch }: AugmentedActionContext,
    payload: UpdateableSolution,
  ): void;
  [ActionTypes.SET_IDEA_FAVORITE](
    { commit, dispatch }: AugmentedActionContext,
    payload: FavoriteIdea,
  ): void;
  [ActionTypes.RESOLVE_CHALLENGE](
    { commit, dispatch }: AugmentedActionContext,
    payload: ResolutionForm,
  ): void;
  [ActionTypes.DELETE_CHALLENGE](
    { commit, dispatch }: AugmentedActionContext,
    payload: string,
  ): Promise<boolean | void>;
  [ActionTypes.GENERATE_PLAN](
    { commit, dispatch }: AugmentedActionContext,
    payload: GeneratePlan,
  ): Promise<string | void>;
  [ActionTypes.GET_ENTITIES]({
    commit,
    dispatch,
  }: AugmentedActionContext): Promise<boolean | void>;
}

// Define actions
export const actions: ActionTree<LocalState, RootState> & Actions = {
  [ActionTypes.GET_CHALLENGE]({ commit, dispatch }, payload) {
    commit(MutationTypes.SET_LOADING, true);
    commit(MutationTypes.SET_CHALLENGE, null);

    return getChallenge(payload)
      .then((response) => {
        const challenge: Challenge = response.data;
        commit(MutationTypes.SET_LOADING, false);
        commit(MutationTypes.SET_CHALLENGE, challenge);
      })
      .catch(function (error) {
        captureException(error);
        dispatch("messages/ADD_ERROR_MESSAGE", "ERROR_CHALLENGE", {
          root: true,
        });
        router.push({ name: "home" });
        commit(MutationTypes.SET_LOADING, false);
      });
  },
  [ActionTypes.UPDATE_CHALLENGE]({ dispatch }, payload) {
    return updateChallenge(payload)
      .then(() => {
        // This method can be called:
        // - when the user has explicitly updated the challenge values
        // - when the auto-generated title asynchronously returns from the API
        // When it's the former, we want to let the user know it has completed
        // When it's the latter, we want to do it quietly in the background
        if (!payload.quiet) {
          dispatch("messages/ADD_INFO_MESSAGE", "CHALLENGE_UPDATED", {
            root: true,
          });
          dispatch("challenges/GET_ACTIVE_CHALLENGES", null, { root: true });
          dispatch("learning/CLEAR_LEARNING", null, { root: true });
          dispatch("sparks/CLEAR_SPARKS", null, { root: true });
        }
      })
      .catch(function (error) {
        captureException(error);
        dispatch("messages/ADD_ERROR_MESSAGE", "ERROR", { root: true });
      });
  },
  [ActionTypes.GET_AI_CHALLENGE_TITLE]({ commit, dispatch, state }) {
    commit(MutationTypes.SET_LOADING_CHALLENGE_TITLE, true);

    let challengeTitle = state.challenge?.description || "";

    return getAIChallengeTitle(challengeTitle)
      .then((response) => {
        challengeTitle = response.data.challenge_title;

        // check if GPT response is "no title" or "No title".
        // if no title, use truncated challenge text
        if (challengeTitle.toLowerCase() === "no title") {
          challengeTitle = truncate(challengeTitle, 40);
        }

        commit(MutationTypes.SET_CHALLENGE_TITLE, challengeTitle);

        // if the challenge already exists in the database, store its title
        if (state.challenge?._id) {
          dispatch(ActionTypes.UPDATE_CHALLENGE, {
            _id: state.challenge._id,
            quiet: true,
            data: {
              title: state.challenge.title,
              description: state.challenge.description,
              subject: state.challenge.subject,
              types: state.challenge.types,
              tags: state.challenge.tags,
            },
          });
        }
      })
      .catch((error) => {
        captureException(error);
        // on error, set a truncated challenge text as title
        commit(MutationTypes.SET_CHALLENGE_TITLE, truncate(challengeTitle, 40));
      })
      .finally(() => {
        commit(MutationTypes.SET_LOADING_CHALLENGE_TITLE, false);
      });
  },
  [ActionTypes.ADD_IDEA]({ commit, dispatch, state }, payload) {
    commit(MutationTypes.SET_LOADING, true);
    const challengeId = state.challenge?._id || "";
    return addIdea(payload, challengeId)
      .then((response) => {
        const idea: Idea = response.data;
        commit(MutationTypes.ADD_IDEA, idea);
        dispatch("messages/ADD_INFO_MESSAGE", "IDEA_CREATED", { root: true });
      })
      .catch(function (error) {
        captureException(error);
        dispatch("messages/ADD_ERROR_MESSAGE", "ERROR", { root: true });
      })
      .finally(() => {
        commit(MutationTypes.SET_LOADING, false);
      });
  },
  [ActionTypes.DELETE_IDEA]({ dispatch, state }, payload) {
    const challengeId = state.challenge?._id || "";
    return deleteIdea(payload._id, challengeId)
      .then(() => {
        dispatch(ActionTypes.GET_CHALLENGE, challengeId);
        dispatch("messages/ADD_INFO_MESSAGE", "IDEA_DELETED", { root: true });
      })
      .catch(function (error) {
        captureException(error);
        dispatch("messages/ADD_ERROR_MESSAGE", "ERROR", { root: true });
      });
  },
  [ActionTypes.UPDATE_IDEA]({ dispatch, state }, payload) {
    const challengeId = state.challenge?._id || "";
    return updateIdea(payload, challengeId)
      .then(() => {
        dispatch(ActionTypes.GET_CHALLENGE, challengeId);
        dispatch("messages/ADD_INFO_MESSAGE", "IDEA_UPDATED", { root: true });
      })
      .catch(function (error) {
        captureException(error);
        dispatch("messages/ADD_ERROR_MESSAGE", "ERROR", { root: true });
      });
  },
  [ActionTypes.ADD_SOLUTION]({ commit, dispatch, state, rootState }, payload) {
    commit(MutationTypes.SET_LOADING, true);
    const challengeId = state.challenge?._id || "";
    return addSolution(payload, challengeId)
      .then((response) => {
        const solution: Solution = response.data;
        commit(MutationTypes.ADD_SOLUTION, solution);

        // user flow - have they added a solution before?
        if (rootState.user.user?.flow.solution) {
          dispatch("messages/ADD_INFO_MESSAGE", "SOLUTION_CREATED", {
            root: true,
          });
          // this is the first solution this user has added, so show specific message
        } else {
          dispatch(
            "user/UPDATE_USER_FLOW",
            { key: "solution", data: { value: true } },
            { root: true },
          );
          dispatch("messages/ADD_SUCCESS_MESSAGE", "FIRST_SOLUTION", {
            root: true,
          });
        }
        return new Promise<string>((resolve) => {
          resolve(solution._id);
        });
      })
      .catch(function (error) {
        captureException(error);
        dispatch("messages/ADD_ERROR_MESSAGE", "ERROR", { root: true });
      })
      .finally(() => {
        commit(MutationTypes.SET_LOADING, false);
      });
  },
  [ActionTypes.DELETE_SOLUTION]({ dispatch, state }, payload) {
    const challengeId = state.challenge?._id || "";
    return deleteSolution(payload._id, challengeId)
      .then(() => {
        dispatch(ActionTypes.GET_CHALLENGE, challengeId);
        dispatch("messages/ADD_INFO_MESSAGE", "SOLUTION_DELETED", {
          root: true,
        });
      })
      .catch(function (error) {
        captureException(error);
        dispatch("messages/ADD_ERROR_MESSAGE", "ERROR", { root: true });
      });
  },
  [ActionTypes.UPDATE_SOLUTION]({ dispatch, state }, payload) {
    const challengeId = state.challenge?._id || "";
    return updateSolution(payload, challengeId)
      .then(() => {
        dispatch(ActionTypes.GET_CHALLENGE, challengeId);
        dispatch("messages/ADD_INFO_MESSAGE", "SOLUTION_UPDATED", {
          root: true,
        });
      })
      .catch(function (error) {
        captureException(error);
        dispatch("messages/ADD_ERROR_MESSAGE", "ERROR", { root: true });
      });
  },
  [ActionTypes.SET_IDEA_FAVORITE]({ dispatch, commit, state }, payload) {
    // update UI immediately
    commit(MutationTypes.UPDATE_IDEA_FAVORITE, {
      _id: payload._id,
      isFavorite: payload.data.isFavorite,
    });

    const challengeId = state.challenge?._id || "";
    return setIdeaFavorite(payload, challengeId).catch(function (error) {
      // if the API call didn't complete successfully, reverse the local update
      commit(MutationTypes.UPDATE_IDEA_FAVORITE, {
        _id: payload._id,
        isFavorite: !payload.data.isFavorite,
      });

      captureException(error);
      dispatch("messages/ADD_ERROR_MESSAGE", "ERROR", { root: true });
    });
  },
  [ActionTypes.RESOLVE_CHALLENGE]({ dispatch, rootState }, payload) {
    return resolveChallenge(payload)
      .then(() => {
        // user flow - have they resolved a challenge before?
        if (rootState.user.user?.flow.resolution) {
          dispatch("messages/ADD_INFO_MESSAGE", "CHALLENGE_RESOLVED", {
            root: true,
          });
          // this is the first resolution this user has added, so show specific message
        } else {
          dispatch(
            "user/UPDATE_USER_FLOW",
            { key: "resolution", data: { value: true } },
            { root: true },
          );
          dispatch("messages/ADD_SUCCESS_MESSAGE", "FIRST_RESOLUTION", {
            root: true,
          });
        }
      })
      .catch(function (error) {
        captureException(error);
        dispatch("messages/ADD_ERROR_MESSAGE", "ERROR", { root: true });
      });
  },
  [ActionTypes.DELETE_CHALLENGE]({ commit, dispatch }, payload) {
    commit(MutationTypes.SET_DELETING, true);
    return deleteChallenge(payload)
      .then(() => {
        commit(MutationTypes.SET_DELETING, false);
        dispatch("challenges/GET_ACTIVE_CHALLENGES", null, { root: true });
        dispatch("challenges/GET_RESOLVED_CHALLENGES", null, { root: true });
        dispatch("messages/ADD_INFO_MESSAGE", "CHALLENGE_DELETED", {
          root: true,
        });

        return new Promise<boolean>((resolve) => {
          resolve(true);
        });
      })
      .catch(function (error) {
        captureException(error);
        dispatch("messages/ADD_ERROR_MESSAGE", "ERROR", { root: true });
        commit(MutationTypes.SET_DELETING, false);
      });
  },
  [ActionTypes.GENERATE_PLAN]({ commit, dispatch }, payload) {
    commit(MutationTypes.SET_LOADING_PLAN, true);
    return generatePlan(payload)
      .then((response) => {
        const plan = response.data.plan;
        commit(MutationTypes.SET_LOADING_PLAN, false);

        return new Promise<string>((resolve) => {
          resolve(plan);
        });
      })
      .catch(function (error) {
        captureException(error);
        commit(MutationTypes.SET_LOADING_PLAN, false);
        dispatch("messages/ADD_ERROR_MESSAGE", "ERROR", { root: true });
      });
  },
  [ActionTypes.GET_ENTITIES]({ commit, dispatch, state }) {
    const challengeText = state.challenge?.description || "";
    // use GPT for entities (useGPT = true)
    return getEntitiesNlp(challengeText, "true")
      .then((response) => {
        commit(MutationTypes.SET_ENTITIES, response.data);

        // return a boolean, were any entities extracted?
        return new Promise<boolean>((resolve) => {
          resolve(response.data.length > 0);
        });
      })
      .catch(function (error) {
        captureException(error);
        dispatch("messages/ADD_ERROR_MESSAGE", "ERROR", { root: true });
      });
  },
};
